import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  Phone,
  MessageSquare,
  Mail,
  StickyNote,
  CalendarClock,
  RefreshCw,
  ListChecks,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { activitiesApi } from "@/lib/crmApi";
import { useSettings } from "@/context/SettingsContext";
import { formatDateTime, getInitials } from "@/lib/crmHelpers";

const ICON_BY_TYPE = {
  appel: Phone,
  sms: MessageSquare,
  email: Mail,
  note: StickyNote,
  statut: CheckCircle2,
  priorite: Sparkles,
  relance: CalendarClock,
  creation: Sparkles,
  procedure: ListChecks,
  programme: ListChecks,
};

const COLOR_BY_TYPE = {
  appel: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300",
  sms: "text-blue-600 bg-blue-50 dark:bg-blue-950/40 dark:text-blue-300",
  email: "text-violet-600 bg-violet-50 dark:bg-violet-950/40 dark:text-violet-300",
  note: "text-zinc-600 bg-zinc-100 dark:bg-zinc-800 dark:text-zinc-300",
  statut: "text-amber-600 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-300",
  creation: "text-[hsl(var(--accent-gold))] bg-amber-50 dark:bg-amber-950/40",
  procedure: "text-blue-600 bg-blue-50 dark:bg-blue-950/40 dark:text-blue-300",
  programme: "text-[hsl(var(--accent-navy))] bg-blue-50 dark:bg-blue-950/40",
  relance: "text-red-600 bg-red-50 dark:bg-red-950/40 dark:text-red-300",
  priorite: "text-red-600 bg-red-50 dark:bg-red-950/40 dark:text-red-300",
};

export default function RecentActivitiesSection() {
  const navigate = useNavigate();
  const { activeProjectId } = useSettings();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await activitiesApi.recent(15, activeProjectId);
      setItems(data || []);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjectId]);

  return (
    <section data-testid="recent-activities-section">
      <div className="mb-4 flex items-end justify-between">
        <div>
          <div className="text-label text-muted-foreground">Timeline</div>
          <h2 className="font-heading mt-1 flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Activity className="h-6 w-6 text-[hsl(var(--accent-navy))]" />
            Activités récentes
          </h2>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          data-testid="recent-activities-refresh"
          className="gap-1.5 text-xs"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Rafraîchir
        </Button>
      </div>

      <div className="rounded-xl border border-border/60 bg-card">
        {items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-muted-foreground">
            <Activity className="h-7 w-7 opacity-40" />
            <span>Aucune activité enregistrée pour le moment.</span>
          </div>
        ) : (
          <ol className="divide-y divide-border/60">
            {items.map((it) => {
              const Icon = ICON_BY_TYPE[it.type] || Activity;
              const color = COLOR_BY_TYPE[it.type] || COLOR_BY_TYPE.note;
              return (
                <li
                  key={`${it.lead_id}-${it.id}`}
                  data-testid={`activity-item-${it.id}`}
                  className="group flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors hover:bg-muted/40"
                  onClick={() => navigate(`/leads/${it.lead_id}`)}
                >
                  <div className={`flex h-8 w-8 flex-none items-center justify-center rounded-full ${color}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="font-heading text-sm font-semibold">
                        {it.lead_nom || "Lead"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        · {it.description}
                      </span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                      <span>{formatDateTime(it.date)}</span>
                      {it.commercial && (
                        <>
                          <span>·</span>
                          <span className="inline-flex items-center gap-1">
                            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-muted text-[8px] font-bold">
                              {getInitials(it.commercial)}
                            </span>
                            {it.commercial}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </section>
  );
}
